/**
 * Register.js
 * Author: Markus.van.Kempen@gmail.com
 * Date: 2nd Nov 2025
 */

import React, { useState } from 'react';
import { useNavigate, Link, useSearchParams, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useToast } from './ToastContainer';
import CustomCaptcha from './CustomCaptcha';
import LoadingSpinner from './LoadingSpinner';
import { getIcon } from '../utils/icons';
import './Login.css';
import './Register.css';
import { resolveReturnPath } from '../utils/safeReturnPath';
import SEO from './SEO';
import { useLanguage } from '../context/LanguageContext';
import { localizeAuthError } from '../i18n/serverErrors';

const Register = () => {
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
    newsletterSubscribed: false,
  });
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [captchaValue, setCaptchaValue] = useState(null);
  
  const { register } = useAuth();
  const { showSuccess, showError } = useToast();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  // New accounts are never admins — never send them to /admin after signup
  const returnUrl = resolveReturnPath(searchParams, location.state, '/', { forbidAdmin: true });
  const loginHref =
    returnUrl !== '/' ? `/login?returnUrl=${encodeURIComponent(returnUrl)}` : '/login';
  const isLocalDev = /localhost|127\.0\.0\.1|\[::1\]/i.test(window.location.hostname);

  const calculatePasswordStrength = (password) => {
    let strength = 0;
    if (password.length >= 6) strength++;
    if (password.length >= 10) strength++;
    if (/[a-z]/.test(password) && /[A-Z]/.test(password)) strength++;
    if (/\d/.test(password)) strength++;
    if (/[^a-zA-Z0-9]/.test(password)) strength++;
    return Math.min(strength, 5);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === 'checkbox' ? checked : value;
    setFormData(prev => ({ ...prev, [name]: newValue }));
    
    // Clear errors
    if (error) setError('');
    if (fieldErrors[name]) {
      setFieldErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }

    // Calculate password strength
    if (name === 'password') {
      setPasswordStrength(calculatePasswordStrength(value));
    }

    // Real-time validation
    validateField(name, newValue);
  };

  const validateField = (name, value) => {
    const errors = {};

    if (name === 'username' && value) {
      if (value.length < 3) {
        errors.username = t.auth.errUsernameShort;
      } else if (!/^[a-zA-Z0-9_-]+$/.test(value)) {
        errors.username = t.auth.errUsernameChars;
      }
    }

    if (name === 'email' && value) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(value)) {
        errors.email = t.auth.errEmailInvalid;
      }
    }

    if (name === 'password' && value) {
      if (value.length < 6) {
        errors.password = t.auth.errPasswordShort;
      }
    }

    if (name === 'confirmPassword' && value) {
      if (value !== formData.password) {
        errors.confirmPassword = t.auth.errPasswordMismatch;
      }
    }

    setFieldErrors(prev => ({ ...prev, ...errors }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    const errors = {};
    
    if (!formData.username) {
      errors.username = t.auth.errUsernameRequired;
    } else if (formData.username.length < 3) {
      errors.username = t.auth.errUsernameShort;
    }
    
    if (!formData.email) {
      errors.email = t.auth.errEmailRequired;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = t.auth.errEmailInvalid;
    }
    
    if (!formData.password) {
      errors.password = t.auth.errPasswordRequired;
    } else if (formData.password.length < 6) {
      errors.password = t.auth.errPasswordShort;
    }
    
    if (!formData.confirmPassword) {
      errors.confirmPassword = t.auth.errConfirmRequired;
    } else if (formData.password !== formData.confirmPassword) {
      errors.confirmPassword = t.auth.errPasswordMismatch;
    }

    if (!captchaValue) {
      setError(t.auth.errCaptcha);
      showError(t.auth.errCaptcha);
      return;
    }
    
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      setError(t.auth.errFixBelow);
      return;
    }
    
    setIsLoading(true);
    setError('');
    setFieldErrors({});

    try {
      const result = await register(
        formData.username, 
        formData.password, 
        formData.email, 
        formData.firstName, 
        formData.lastName, 
        formData.newsletterSubscribed
      );
      
      if (result.success) {
        // Save initial profile data to localStorage
        const initialProfile = {
          firstName: formData.firstName || '',
          lastName: formData.lastName || '',
          email: formData.email || '',
          phone: '',
          shippingAddress: '',
          city: '',
          province: '',
          postalCode: '',
          country: '',
          newsletter: formData.newsletterSubscribed || false,
        };
        localStorage.setItem(`userProfile_${formData.username}`, JSON.stringify(initialProfile));
        
        if (result.devVerificationUrl && isLocalDev) {
          console.log('[Register] Dev verification link:', result.devVerificationUrl);
          showSuccess(t.auth.welcomeCheckEmailDev.replace('{name}', formData.username));
        } else {
          showSuccess(t.auth.welcomeCheckEmail.replace('{name}', formData.username));
        }
        setTimeout(() => navigate(returnUrl, { replace: true }), 500);
      } else {
        setError(localizeAuthError(result.error, t.auth, 'errRegistrationFailed'));
        showError(localizeAuthError(result.error, t.auth, 'errRegistrationFailedRetry'));
        setIsLoading(false);
      }
    } catch (err) {
      const message = localizeAuthError(err.message, t.auth, 'errRegistrationGeneric');
      setError(message);
      showError(message);
      setIsLoading(false);
    }
  };

  return (
    <div className="register-container">
      <SEO
        title="Create Account | Cranford Publications"
        description="Create a Cranford Publications account."
        canonical="/register"
        robots="noindex, follow"
      />
      <h2>{t.auth.signUpTitle}</h2>
      
      <div className="register-feature-callout">
        <span className="feature-icon">🎵</span>
        <div className="feature-text-content">
          <strong>{t.auth.playerCalloutTitle}</strong>
          <span className="feature-subtext">{t.auth.playerCalloutText}</span>
        </div>
      </div>
      
      {isLoading && (
        <div className="loading-spinner-fullscreen">
          <LoadingSpinner message={t.auth.creatingAccountSpinner} />
        </div>
      )}
      
      <p className="register-subtitle">
        {t.auth.alreadyHaveAccount} <Link to={loginHref}>{getIcon('login')} {t.auth.signInHere}</Link> ·{' '}
        <Link to="/forgot-password">{getIcon('mail')} {t.auth.forgotYourPassword}</Link>
      </p>
      {isLocalDev && (
        <p className="register-dev-hint">{t.auth.devHint}</p>
      )}
      
      <div className="profile-section">
        <form onSubmit={handleSubmit} className="register-form">
          {error && <p className="error-message">{error}</p>}
        
        <div className="form-two-column">
          <div className="form-column">
            <div className="input-group">
              <label htmlFor="firstName">{t.auth.firstName}</label>
              <input
                type="text"
                id="firstName"
                name="firstName"
                value={formData.firstName}
                onChange={handleInputChange}
              />
            </div>
            
            <div className="input-group">
              <label htmlFor="username">{t.auth.username} *</label>
              <input
                type="text"
                id="username"
                name="username"
                value={formData.username}
                onChange={handleInputChange}
                className={fieldErrors.username ? 'error' : ''}
                required
              />
              {fieldErrors.username && <span className="field-error">{fieldErrors.username}</span>}
            </div>
            
            <div className="input-group">
              <label htmlFor="password">{t.auth.password} *</label>
              <input
                type="password"
                id="password"
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                className={fieldErrors.password ? 'error' : ''}
                required
                minLength="6"
              />
              {fieldErrors.password && <span className="field-error">{fieldErrors.password}</span>}
              {formData.password && (
                <div className="password-strength">
                  <div className="strength-meter">
                    <div 
                      className={`strength-bar strength-${passwordStrength}`}
                      style={{ width: `${(passwordStrength / 5) * 100}%` }}
                    />
                  </div>
                  <span className="strength-label">
                    {passwordStrength === 0 && t.auth.strengthTooWeak}
                    {passwordStrength === 1 && t.auth.strengthWeak}
                    {passwordStrength === 2 && t.auth.strengthFair}
                    {passwordStrength === 3 && t.auth.strengthGood}
                    {passwordStrength === 4 && t.auth.strengthStrong}
                    {passwordStrength === 5 && t.auth.strengthVeryStrong}
                  </span>
                </div>
              )}
            </div>
          </div>
          
          <div className="form-column">
            <div className="input-group">
              <label htmlFor="lastName">{t.auth.lastName}</label>
              <input
                type="text"
                id="lastName"
                name="lastName"
                value={formData.lastName}
                onChange={handleInputChange}
              />
            </div>
            
            <div className="input-group">
              <label htmlFor="email">{t.auth.email} *</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                className={fieldErrors.email ? 'error' : ''}
                required
              />
              {fieldErrors.email && <span className="field-error">{fieldErrors.email}</span>}
            </div>
            
            <div className="input-group">
              <label htmlFor="confirmPassword">{t.auth.confirmPassword} *</label>
              <input
                type="password"
                id="confirmPassword"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleInputChange}
                className={fieldErrors.confirmPassword ? 'error' : ''}
                required
              />
              {fieldErrors.confirmPassword && <span className="field-error">{fieldErrors.confirmPassword}</span>}
            </div>
          </div>
        </div>
        
        <div className="input-group-checkbox">
          <input
            type="checkbox"
            id="newsletterSubscribed"
            name="newsletterSubscribed"
            checked={formData.newsletterSubscribed}
            onChange={handleInputChange}
          />
          <label htmlFor="newsletterSubscribed">{t.auth.newsletterOptIn}</label>
        </div>
        
        <div className="captcha-submit-row">
          <div className="captcha-container">
            <CustomCaptcha onVerify={(verified) => setCaptchaValue(verified)} />
          </div>

          <button type="submit" disabled={isLoading || !captchaValue}>
            {isLoading
              ? t.auth.creatingYourAccount
              : !captchaValue
                ? t.auth.completeVerificationToRegister
                : t.auth.createAccount}
          </button>
        </div>
        </form>
      </div>
    </div>
  );
};

export default Register; 